/**
 * Session Management Action Hooks
 *
 * These hooks provide high-level session management actions
 * that convert Stytch responses to our domain types.
 */
import { discoveryOrganizationTimestamps } from './discovery-organization-timestamps';
import { decodeJWTSafe } from '../browser-utils';
import { ROLES } from '@sandstone/types';
import { useCallback } from 'react';
import { useStytchWrappers } from './stytch-hooks';
import { useAuthContext } from './core-hooks';
/**
 * Hook to authenticate existing session
 */
export function useAuthenticateSession() {
    const { authenticateSession } = useStytchWrappers();
    return useCallback(async (options) => {
        const result = await authenticateSession(options);
        if (result.success && result.member && result.organization && result.session_jwt) {
            // This JWT came from successful Stytch authentication. These decoded values
            // hydrate client state only; server authorization still verifies the session.
            // Preserve legacy nested metadata, then the root/custom layouts stamped by
            // our session claim generators. Never consume untrusted_metadata.
            const payload = decodeJWTSafe(result.session_jwt);
            const claimSources = [payload?.trusted_metadata, payload, payload?.custom_claims].filter((value) => typeof value === 'object' && value !== null && !Array.isArray(value));
            const metadataString = (key) => {
                for (const source of claimSources) {
                    const value = source[key];
                    if (typeof value === 'string' && value.trim())
                        return value;
                }
                return undefined;
            };
            // Standard root/custom roles take precedence, matching server/edge normalization.
            // Legacy singular metadata roles remain a fallback for older sessions.
            const customClaims = claimSources.find(source => source === payload?.custom_claims);
            const roleClaims = [payload?.roles, customClaims?.roles, payload?.role]
                .map(value => Array.isArray(value)
                ? value.filter((item) => typeof item === 'string' && !!item.trim())
                : typeof value === 'string' && value.trim()
                    ? [value]
                    : [])
                .find(value => value.length > 0);
            const normalizedRoles = roleClaims ??
                claimSources
                    .map(source => (typeof source.role === 'string' ? source.role : undefined))
                    .filter((value) => value !== undefined);
            const role = normalizedRoles
                .map(value => {
                if (value === 'workspace_owner')
                    return 'owner';
                if (value === 'workspace_admin' || value === 'stytch_admin')
                    return 'admin';
                return ROLES.find(candidate => candidate === value);
            })
                .find(value => value !== undefined) ?? 'member';
            const annualBilling = claimSources.map(source => source.is_annual_billing).find(value => typeof value === 'boolean') ??
                claimSources.map(source => source.is_anual_billing).find(value => typeof value === 'boolean');
            const timestamps = discoveryOrganizationTimestamps(result.organization);
            // Convert Stytch member to our UserProfile type
            const userProfile = {
                id: metadataString('user_id') || '',
                email: result.member.email_address,
                name: result.member.name || '',
                role,
                workspace_id: metadataString('workspace_id') || '',
                created_at: result.member.created_at,
                updated_at: result.member.updated_at,
                stytch_member_id: result.member.member_id,
                stytch_organization_id: result.organization.organization_id,
                permissions: [], // Should be extracted from JWT or database
                roles: [], // Should be extracted from JWT or database
            };
            // Convert Stytch organization to our Workspace type
            const workspace = {
                id: metadataString('workspace_id') || '',
                name: result.organization.organization_name,
                description: undefined,
                company_logo_path: result.organization.organization_logo_url,
                domain: result.organization.organization_slug,
                company_domain: result.organization.organization_slug,
                number_of_users: 0, // Would need to fetch this separately
                plan_type: 'free', // Default, should be fetched from database
                is_active: true,
                subscription_amount: undefined,
                subscription_status: undefined,
                trial_start_date: undefined,
                trial_end_date: undefined,
                current_period_end: undefined,
                seat_count: undefined,
                created_at: metadataString('created_at') || timestamps.created_at,
                updated_at: metadataString('updated_at') || timestamps.updated_at,
                created_by: metadataString('created_by') || result.member.member_id,
                country: metadataString('country'),
                stytch_organization_id: metadataString('stytch_organization_id') || result.organization.organization_id,
                last_active_at: metadataString('last_active_at'),
                is_annual_billing: annualBilling,
                stripe_customer_id: metadataString('stripe_customer_id'),
                stripe_subscription_id: metadataString('stripe_subscription_id'),
            };
            return {
                success: true,
                sessionJwt: result.session_jwt,
                sessionToken: result.session_token,
                memberSession: result.member_session,
                userProfile,
                workspace,
                data: result.data,
            };
        }
        else {
            return {
                success: false,
                error: result.error || 'No valid session found',
                data: result.data,
            };
        }
    }, [authenticateSession]);
}
/**
 * Hook to logout/revoke session
 * Combines Stytch logout with local auth state clearing
 */
export function useLogout() {
    const { logout } = useAuthContext();
    const { revokeSession } = useStytchWrappers();
    return useCallback(async () => {
        // First revoke the Stytch session
        await revokeSession();
        // Then clear local auth state
        await logout();
        return { success: true };
    }, [logout, revokeSession]);
}
/**
 * Hook to refresh authentication
 * Combines Stytch session refresh with local auth state update
 */
export function useRefreshAuth() {
    const { refreshAuth } = useAuthContext();
    const { authenticateSession } = useStytchWrappers();
    return useCallback(async () => {
        // First refresh the Stytch session
        await authenticateSession();
        // Then refresh local auth state
        await refreshAuth();
        return { success: true };
    }, [refreshAuth, authenticateSession]);
}
